import React, { Fragment, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { useSortBy } from 'react-instantsearch';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import ArrowDown from '@/customercenter-ui/icons/arrowDown';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import { useProductListingContext } from './components/context/productListingContext';

export function SortBy() {
  const projectSettings: ProjectSettings = useProjSettings();
  const virtualAlgoliaConfig = projectSettings?.isVirtualAlgoliaSortByEnabled
    ? JSON.parse(projectSettings?.isVirtualAlgoliaSortByEnabled)
    : {};
  const isVirtualAlgoliaEnabledSortBySite = virtualAlgoliaConfig?.[`${getSelectedDivision()}Site`];
  const { sortByOptions } = useProductListingContext();
  let sortByOptionsCSA = [];
  try {
    const options = projectSettings?.isVirtualAlgoliaSortByOptions;
    if (typeof options === 'string') {
      const parsed = JSON.parse(options);
      if (
        Array.isArray(parsed) &&
        parsed.every((option) => typeof option === 'object' && option.label && option.value)
      ) {
        sortByOptionsCSA = parsed;
      } else {
        console.warn('Parsed sort options are invalid. Falling back to default options.');
      }
    }
  } catch (error) {
    console.error('Failed to parse projectSettings.isVirtualAlgoliaSortByOptions:', error);
  }
  const activeSortByOptions =
    isVirtualAlgoliaEnabledSortBySite === 'true' || isVirtualAlgoliaEnabledSortBySite === true
      ? sortByOptionsCSA.map((opt) => {
          return {
            ...opt,
            value: opt.value.replace('{siteId}', `${getSelectedDivision()}Site`),
          };
        })
      : sortByOptions;

  const { currentRefinement, options, refine } = useSortBy({
    items: activeSortByOptions,
  });
  const [selectedOption, setSelected] = useState(() => options.find((option) => option.value === currentRefinement));
  const handleChange = (event: any) => {
    const selectedValue = event.value;
    sessionStorage.setItem('manualSortChange', 'true');
    const selected = options.find((opt) => opt.value === selectedValue);
    setSelected(selected);
    refine(selectedValue);
  };

  return (
    <div className="relative flex items-center">
      <label className="mr-2 hidden text-sm font-medium leading-[19.6px] lg:block">
        {mapLocaleToMeaningfulFormat().sortBy}
      </label>
      <div className="w-full lg:w-[205px]">
        <Listbox value={selectedOption} onChange={handleChange}>
          {({ open }) => {
            return (
              <div className="relative">
                <ListboxButton
                  className={`relative flex h-11 w-full cursor-default items-center justify-center gap-2 border  border-foggyGray bg-white py-2 text-sm font-bold text-black focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm lg:justify-between lg:pl-[10px] lg:pr-10 lg:font-normal ${
                    open ? 'rounded-t-md' : 'rounded-md'
                  }`}
                >
                  <span className="hidden truncate font-bold lg:block">{selectedOption?.label}</span>
                  <span className="text-base font-bold leading-[20.8px] lg:hidden">Sort</span>
                  <span className="pointer-events-none inset-y-0 -right-1 flex items-center pr-2 lg:absolute">
                    <ArrowDown className={`fill-black transition-transform ${open ? 'rotate-180' : 'rotate-0'}`} />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-[11] -mt-[1px] max-h-60 w-full overflow-auto rounded-b-md border border-foggyGray bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {options.map((option, optionIdx) => (
                      <ListboxOption
                        key={optionIdx}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pr-4 ${active ? 'text-black' : 'text-black'}`
                        }
                        value={option}
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate px-[10px] ${selected ? 'font-bold' : 'font-normal'}`}>
                              {option.label}
                            </span>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </div>
            );
          }}
        </Listbox>
      </div>
    </div>
  );
}
